import React, { useEffect } from 'react';
import { FormControlLabel, Grid2, Switch } from '@mui/material';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEditVenueIntegration, useGetIntegrations } from '@openapi/integrations/integrations';
import IntegrationCard, { INTEGRATION_NAMES } from '../integrations/components/IntegrationCard';
import DynamicPricingForm from './components/DynamicPricingForm';

function EditVenueIntegrationPage() {
    const { t } = useTranslation();
    const { venueId, integrationType } = useParams();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { mutateAsync: editVenueIntegration } = useEditVenueIntegration();

    // Fetch all integrations for this venue
    const {
        data,
        isLoading: loading,
        error,
    } = useGetIntegrations(
        {
            optionalVenueId: venueId,
            pageSize: 100,
        },
        {
            enabled: !!venueId,
        },
    );

    useEffect(() => {
        if (error) {
            navigate(`/venues/${venueId}/integrations`);
            notifyError(error);
        }
    }, [error, navigate, notifyError, venueId]);

    if (loading) {
        return true;
    }

    // Find the existing integration for this type
    const existingIntegration = data?.integrations?.find((i) => i.type === integrationType);

    const defaultIntegration = {
        venueId: parseInt(venueId, 10),
        type: integrationType,
        settings: {},
        enabled: false,
    };

    const initialValues = existingIntegration || defaultIntegration;

    const onSubmit = async (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editVenueIntegration({
                        id: Number(venueId),
                        data: {
                            type: values.type,
                            settings: values.settings,
                            enabled: values.enabled,
                        },
                    });
                    navigate(`/venues/${venueId}/integrations`);
                    notifySuccess(t('integrations.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const validateSchema = Yup.object().shape({
        type: Yup.string().required(t('common.required')),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue }) => (
                <Form>
                    <PageWithTitle
                        title={`${t('integrations.title')} - ${
                            INTEGRATION_NAMES[integrationType] ||
                            integrationType.charAt(0).toUpperCase() + integrationType.slice(1)
                        }`}
                    >
                        <Grid2 container spacing={3}>
                            {/* Sidebar with Integration Card */}
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <IntegrationCard integration={values} compact>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.enabled}
                                                onChange={(e) =>
                                                    setFieldValue('enabled', e.target.checked)
                                                }
                                            />
                                        }
                                        label={t('integrations.columns.enabled')}
                                        labelPlacement="end"
                                        sx={{ mx: 0 }}
                                    />
                                </IntegrationCard>
                            </Grid2>

                            {/* Configuration Form */}
                            <Grid2 size={{ xs: 12, md: 9 }}>
                                <BlockWithTitle title={t('integrations.configuration')}>
                                    <Grid2
                                        container
                                        spacing={2}
                                        size={{ xs: 12 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        {values.type === 'dynamic_pricing' && (
                                            <DynamicPricingForm values={values} />
                                        )}

                                        {/* Action Buttons */}
                                        <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                                            <Grid2 container spacing={2} justifyContent="flex-end">
                                                <Grid2 size="auto">
                                                    <CommonButton
                                                        onClick={() =>
                                                            navigate(
                                                                `/venues/${venueId}/integrations`,
                                                            )
                                                        }
                                                        label={t('common.cancel')}
                                                        buttonType="secondary"
                                                    />
                                                </Grid2>
                                                <Grid2 size="auto">
                                                    <CommonButton
                                                        disabled={isSubmitting}
                                                        type="submit"
                                                        label={t('common.save')}
                                                        buttonType="primary"
                                                    />
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    </Grid2>
                                </BlockWithTitle>
                            </Grid2>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditVenueIntegrationPage;
